<template>
  <BaseTemplate v-if="!isFullscreenMode" app-name="" page-title="">
    <template #content>
      <div v-if="!isFullscreenMode" 
        class="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 p-4 transition-all duration-300">
        
        <!-- Header com controles -->
        <div class="mb-6">
          <div class="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-6">

            
            <div class="flex gap-3 sm:ml-auto">
              <button @click="setView('cards')" :class="[
                'px-4 py-2 rounded-lg border font-medium transition-all flex items-center gap-2',
                currentView === 'cards'
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
              ]">
                <Building2 class="h-4 w-4" />
                Cards
              </button>
              <button @click="setView('charts')" :class="[
                'px-4 py-2 rounded-lg border font-medium transition-all flex items-center gap-2',
                currentView === 'charts'
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
              ]">
                <BarChart3 class="h-4 w-4" />
                Gráficos
              </button>
              <button @click="toggleFullscreen" :class="[
                'px-4 py-2 rounded-lg border font-medium transition-all flex items-center gap-2',
                'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
              ]">
                <Maximize2 class="h-4 w-4" />
                Tela Cheia
              </button>
            </div>
          </div>
        </div>

        <!-- Mensagem de erro -->
        <div v-if="error"
          class="mb-6 p-4 bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 rounded-lg flex items-center gap-3 shadow-sm">
          <AlertCircle class="h-5 w-5 text-red-500 dark:text-red-300" />
          <p class="font-medium">{{ error }}</p>
        </div>

        <!-- Cards de resumo -->
        <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-6">
          <div v-for="(count, category) in getStatusSummary()" :key="category"
            class="bg-white dark:bg-gray-800 rounded-xl shadow-sm hover:shadow-md border border-gray-100 dark:border-gray-700 p-5 transition-all duration-200 cursor-pointer"
            @click="filterByCategory(category)">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm font-medium text-gray-500 dark:text-gray-400">{{ category }}</p>
                <div class="flex items-baseline mt-1">
                  <p class="text-2xl font-bold" :class="getStatusTextColor(category)">{{ count }}</p>
                </div>
              </div>
              <div class="h-14 w-14 rounded-full flex items-center justify-center" :class="getStatusBgColor(category)">
                <component :is="getStatusIcon(category)" class="h-7 w-7" :class="getStatusIconColor(category)" />
              </div>
            </div>
          </div>
          
          <div class="bg-white dark:bg-gray-800 rounded-xl shadow-sm hover:shadow-md border border-gray-100 dark:border-gray-700 p-5 transition-all duration-200">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm font-medium text-gray-500 dark:text-gray-400">Última Atualização</p>
                <div class="flex items-baseline mt-1">
                  <p class="text-lg font-bold text-gray-900 dark:text-white">
                    {{ lastUpdate ? formatDate(lastUpdate) : "N/A" }}
                  </p>
                </div>
              </div>
              <div class="h-14 w-14 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                <Calendar class="h-7 w-7 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
          </div>
        </div>

        <!-- Filtro ativo -->
        <transition name="fade-scale">
          <div v-show="selectedCategory" class="flex justify-center mb-6">
            <button @click="clearFilter"
              class="text-sm font-medium bg-blue-500 text-white px-3 py-1 rounded-md shadow hover:bg-blue-600 transition">
              Limpar Filtro: {{ selectedCategory }}
            </button>
          </div>
        </transition>

        <!-- Visão de Gráficos -->
        <div v-if="currentView === 'charts'" class="space-y-8">
          <div class="text-center mb-8">
            <h2 class="text-3xl font-bold text-gray-900 dark:text-white mb-2">Análise de Status das Unidades</h2>
            <p class="text-gray-600 dark:text-gray-400">Visão geral do status dos links das unidades</p>
          </div>

          <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <!-- Gráfico de Status Geral -->
            <div class="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg border border-gray-100 dark:border-gray-700">
              <div class="text-center mb-6">
                <h3 class="text-2xl font-bold text-gray-900 dark:text-white mb-2">Distribuição de Status</h3>
                <p class="text-gray-600 dark:text-gray-400">Status atual de todas as unidades</p>
              </div>
              <div class="h-80 flex items-center justify-center">
                <div class="w-full max-w-sm">
                  <Doughnut :data="statusChartData" :options="chartOptions" />
                </div>
              </div>
              <div class="mt-6 grid grid-cols-3 gap-4 text-center">
                <div class="bg-green-50 dark:bg-green-900/20 rounded-lg p-3">
                  <div class="text-2xl font-bold text-green-600 dark:text-green-400">{{ getStatusCount(1) }}</div>
                  <div class="text-sm text-green-700 dark:text-green-300">Pleno</div>
                </div>
                <div class="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-3">
                  <div class="text-2xl font-bold text-yellow-600 dark:text-yellow-400">{{ getStatusCount(2, 3) }}</div>
                  <div class="text-sm text-yellow-700 dark:text-yellow-300">Parcial</div>
                </div>
                <div class="bg-red-50 dark:bg-red-900/20 rounded-lg p-3">
                  <div class="text-2xl font-bold text-red-600 dark:text-red-400">{{ getStatusCount(4) }}</div>
                  <div class="text-sm text-red-700 dark:text-red-300">Offline</div>
                </div>
              </div>
            </div>

            <!-- Gráfico de Barras por Unidade -->
            <div class="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg border border-gray-100 dark:border-gray-700">
              <div class="text-center mb-6">
                <h3 class="text-2xl font-bold text-gray-900 dark:text-white mb-2">Status por Unidade</h3>
                <p class="text-gray-600 dark:text-gray-400">Comparativo individual das unidades</p>
              </div>
              <div class="h-80">
                <Bar :data="unitsBarChartData" :options="barChartOptions" />
              </div>
            </div>
          </div>

          <!-- Gráfico de Linha - Histórico -->
          <div class="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg border border-gray-100 dark:border-gray-700">
            <div class="text-center mb-6">
              <h3 class="text-2xl font-bold text-gray-900 dark:text-white mb-2">Tendência de Disponibilidade</h3>
              <p class="text-gray-600 dark:text-gray-400">Evolução da disponibilidade ao longo do tempo</p>
            </div>
            <div class="h-96">
              <Line :data="trendChartData" :options="lineChartOptions" />
            </div>
            <div class="mt-6 text-center">
              <div class="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                <div class="text-3xl font-bold text-blue-600 dark:text-blue-400">{{ availabilityPercentage }}%</div>
                <div class="text-sm text-blue-700 dark:text-blue-300">Disponibilidade Geral</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Visão de Cards -->
        <div v-if="currentView === 'cards'">
          <transition name="fade-scale">
            <div v-if="filterLoading" class="text-center py-4">
              <RefreshCw class="h-16 w-16 animate-spin text-blue-600 dark:text-blue-400 mx-auto" />
            </div>

            <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div v-for="servidor in displayServidores" :key="servidor.id"
                class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 hover:shadow-lg transition-all duration-200">
                <div class="p-5">
                  <div class="flex justify-between items-start mb-4">
                    <div class="flex items-center gap-3">
                      <div class="bg-gray-100 dark:bg-gray-700 p-2 rounded-lg">
                        <Building2 class="h-5 w-5 text-gray-600 dark:text-gray-300" />
                      </div>
                      <div>
                        <h2 class="font-semibold text-gray-800 dark:text-gray-200">{{ servidor.unidade_nome }}</h2>
                      </div>
                    </div>
                    <span class="flex items-center mt-2 gap-2 font-medium text-sm text-gray-700 dark:text-gray-300">
                      <component :is="getStatusIcon(statusText(servidor.status))"
                        :class="getStatusIconColor(statusText(servidor.status)) + ' h-5 w-5'" />
                      <span class="hidden md:inline">{{ statusText(servidor.status) }}</span>
                    </span>
                  </div>

                  <div class="space-y-4">
                    <!-- Link Primário -->
                    <div :class="getLinkCardClass(servidor?.status, 'primary') + ' p-4'">
                      <div class="flex items-center gap-4">
                        <div class="flex items-center justify-center w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-700">
                          <CheckCircle v-if="servidor?.status === 1 || servidor?.status === 2"
                            class="h-6 w-6 text-green-600 dark:text-green-400" />
                          <XCircle v-else class="h-6 w-6 text-red-600 dark:text-white" />
                        </div>
                        <div class="flex-1">
                          <p class="font-medium text-gray-700 dark:text-gray-200">Link Primário</p>
                          <p class="text-sm text-gray-600 dark:text-gray-400 mt-1">
                            {{ servidor?.primaryName || "Desconhecido" }}
                            <span class="text-gray-400 dark:text-gray-500">({{ servidor?.primaryIP || "N/A" }})</span>
                          </p>
                        </div>
                        <div class="flex items-center justify-center w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-700">
                          <RefreshCw v-if="servidor?.primaryIP && servidor?.sync_primario === 'on'"
                            class="h-6 w-6 text-blue-600 dark:text-blue-400 animate-spin" />
                          <RefreshCwOff v-else-if="servidor?.primaryIP && servidor?.sync_primario === 'off'"
                            class="h-6 w-6 text-red-600 dark:text-red-400" />
                          <RefreshCwOff v-else class="h-6 w-6 text-gray-400 dark:text-gray-500" />
                        </div>
                        <button v-if="showGlobeButton(servidor?.status, 'primary')"
                          @click="openLink(servidor?.dns)"
                          class="bg-blue-500 hover:bg-blue-600 text-white rounded-full p-2 shadow transition">
                          <Globe class="h-5 w-5" />
                        </button>
                      </div>
                    </div>

                    <!-- Link Secundário -->
                    <div :class="getLinkCardClass(servidor?.status ?? 4, 'secondary', servidor) + ' p-4'">
                      <div class="flex items-center gap-4">
                        <div class="flex items-center justify-center w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-700">
                          <XCircle v-if="!servidor?.secondaryIP || !servidor?.secondaryName"
                            class="h-6 w-6 text-gray-400 dark:text-gray-500" />
                          <CheckCircle v-else-if="servidor?.status === 1 || servidor?.status === 3"
                            class="h-6 w-6 text-green-600 dark:text-green-400" />
                          <XCircle v-else class="h-6 w-6 text-red-600 dark:text-white" />
                        </div>
                        <div class="flex-1">
                          <p class="font-medium text-gray-700 dark:text-gray-200">Link Secundário</p>
                          <p class="text-sm text-gray-600 dark:text-gray-400 mt-1">
                            {{ servidor?.secondaryName || "Desconhecido" }}
                            <span class="text-gray-400 dark:text-gray-500">({{ servidor?.secondaryIP || "N/A" }})</span>
                          </p>
                        </div>
                        <div class="flex items-center justify-center w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-700">
                          <RefreshCw v-if="servidor?.secondaryIP && servidor?.sync_secundario === 'on'"
                            class="h-6 w-6 text-blue-600 dark:text-blue-400 animate-spin" />
                          <RefreshCwOff v-else-if="servidor?.secondaryIP && servidor?.sync_secundario === 'off'"
                            class="h-6 w-6 text-red-600 dark:text-red-400" />
                          <RefreshCwOff v-else class="h-6 w-6 text-gray-400 dark:text-gray-500" />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="mt-4">
                    <button @click="goToUnidadeDetalhes(servidor.unidade_id)"
                      class="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded transition">
                      Ver detalhes da Unidade
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </transition>
        </div>

      </div>
    </template>
  </BaseTemplate>

  <!-- Modo Tela Cheia -->
  <div v-else class="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white transition-all duration-300">
    <!-- Header do modo tela cheia -->
    <div class="bg-black/30 backdrop-blur-sm border-b border-white/10 p-6">
      <div class="flex justify-between items-center">
        <!-- Logo no canto esquerdo -->
        <div class="flex items-center">
          <img src="@/assets/logo_ccdti.png" alt="Logo CCDTI" class="h-12 w-auto" />
        </div>
        
        <!-- Título centralizado -->
        <div class="flex-1 flex justify-center">
          <div class="transform translate-x-8">
            <h1 class="text-4xl font-bold bg-gradient-to-r from-blue-400 to-cyan-300 bg-clip-text text-transparent">
              Links Unidades
            </h1>
          </div>
        </div>
        
        <!-- Status e última atualização no canto direito -->
        <div class="flex items-center gap-6">
          <div class="flex gap-4">
            <div class="bg-green-500/20 border border-green-400/30 rounded-lg px-4 py-2">
              <span class="text-green-300 text-lg font-semibold">{{ getStatusCount(1) }} Pleno</span>
            </div>
            <div class="bg-yellow-500/20 border border-yellow-400/30 rounded-lg px-4 py-2">
              <span class="text-yellow-300 text-lg font-semibold">{{ getStatusCount(2, 3) }} Parcial</span>
            </div>
            <div class="bg-red-500/20 border border-red-400/30 rounded-lg px-4 py-2">
              <span class="text-red-300 text-lg font-semibold">{{ getStatusCount(4) }} Offline</span>
            </div>
          </div>
          <!-- Card de Última Atualização -->
          <div class="bg-black/30 backdrop-blur-sm border border-white/10 rounded-xl p-4">
            <div class="flex items-center gap-3">
              <div class="h-10 w-10 bg-blue-500/20 border border-blue-400/30 rounded-full flex items-center justify-center">
                <Calendar class="h-5 w-5 text-blue-400" />
              </div>
              <div>
                <p class="text-xs font-medium text-gray-300">Última Atualização</p>
                <p class="text-sm font-bold text-white">
                  {{ lastUpdate ? formatDate(lastUpdate) : "N/A" }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Conteúdo do modo tela cheia -->
    <div class="p-8">
      <!-- Cards em modo tela cheia -->
      <div class="grid grid-cols-3 gap-8">
        <div v-for="servidor in currentPageServidores" :key="servidor.id"
          class="bg-black/30 backdrop-blur-sm border rounded-2xl p-8 min-h-[400px] transition-all duration-300"
          :class="getFullscreenCardBorderClass(servidor.status)">
          
          <!-- Header do servidor -->
          <div class="flex justify-between items-start mb-6">
            <div class="flex items-center gap-3">
              <div :class="getFullscreenStatusIndicatorClass(servidor.status)" class="w-4 h-4 rounded-full animate-pulse"></div>
              <h3 class="text-2xl font-bold text-white">{{ servidor.unidade_nome }}</h3>
            </div>
            <div class="text-right">
              <div class="text-lg font-bold text-white">{{ statusText(servidor.status) }}</div>
            </div>
          </div>

          <!-- Links em modo tela cheia -->
          <div class="space-y-4">
            <!-- Link Primário -->
            <div :class="getFullscreenLinkCardClass(servidor?.status, 'primary', servidor) + ' p-4 rounded-xl border'">
              <div class="flex items-center justify-between mb-3">
                <div class="flex items-center gap-2">
                  <Globe class="h-5 w-5 text-blue-400" />
                  <span class="text-lg font-bold text-white">Link Primário</span>
                </div>
                <div class="flex items-center gap-2">
                  <CheckCircle v-if="servidor?.status === 1 || servidor?.status === 2" class="h-6 w-6 text-green-400" />
                  <XCircle v-else class="h-6 w-6 text-red-400" />
                  <RefreshCw v-if="servidor?.primaryIP && servidor?.sync_primario === 'on'"
                    class="h-5 w-5 text-blue-400 animate-spin" />
                  <RefreshCwOff v-else-if="servidor?.primaryIP && servidor?.sync_primario === 'off'"
                    class="h-5 w-5 text-red-400" />
                  <RefreshCwOff v-else class="h-5 w-5 text-gray-400" />
                </div>
              </div>
              <div class="text-white">
                <div class="text-base">{{ servidor?.primaryName || "Desconhecido" }}</div>
                <div class="text-gray-300 text-sm">{{ servidor?.primaryIP || "N/A" }}</div>
              </div>
            </div>

            <!-- Link Secundário -->
            <div :class="getFullscreenLinkCardClass(servidor?.status ?? 4, 'secondary', servidor) + ' p-4 rounded-xl border'">
              <div class="flex items-center justify-between mb-3">
                <div class="flex items-center gap-2">
                  <Globe class="h-5 w-5 text-blue-400" />
                  <span class="text-lg font-bold text-white">Link Secundário</span>
                </div>
                <div class="flex items-center gap-2">
                  <CheckCircle v-if="servidor?.status === 1 || servidor?.status === 3" class="h-6 w-6 text-green-400" />
                  <XCircle v-else class="h-6 w-6 text-red-400" />
                  <RefreshCw v-if="servidor?.secondaryIP && servidor?.sync_secundario === 'on'"
                    class="h-5 w-5 text-blue-400 animate-spin" />
                  <RefreshCwOff v-else-if="servidor?.secondaryIP && servidor?.sync_secundario === 'off'"
                    class="h-5 w-5 text-red-400" />
                  <RefreshCwOff v-else class="h-5 w-5 text-gray-400" />
                </div>
              </div>
              <div class="text-white">
                <div class="text-base">{{ servidor?.secondaryName || "Desconhecido" }}</div>
                <div class="text-gray-300 text-sm">{{ servidor?.secondaryIP || "N/A" }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Controles do modo tela cheia -->
    <div class="fixed top-4 right-4 flex gap-2">
      <button @click="toggleFullscreen"
        class="px-4 py-2 rounded-lg border font-medium transition-all flex items-center gap-2 bg-red-600 text-white border-red-600 hover:bg-red-700">
        <Minimize2 class="h-4 w-4" />
        Sair
      </button>
    </div>
  </div>
</template>

<script setup>
import BaseTemplate from '../templates/BaseTemplate.vue';
import { ref, onMounted, computed, onUnmounted } from "vue";
import { useRouter } from "vue-router";
import apiClient from '@/services/authService';

// Importações do Chart.js
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
} from 'chart.js'
import { Doughnut, Line, Bar } from 'vue-chartjs'

// Registrar componentes do Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
)

import {
  RefreshCwOff,
  RefreshCw,
  AlertCircle,
  Building2,
  Calendar,
  Monitor,
  MonitorCheck,
  MonitorX,
  CheckCircle,
  XCircle,
  Globe,
  Maximize2,
  Minimize2,
  BarChart3
} from 'lucide-vue-next';

const router = useRouter();

// Estados principais
const servidores = ref([]);
const isLoading = ref(false);
const error = ref(null);
const currentView = ref('cards');
const isFullscreenMode = ref(false);
const fullscreenView = ref('cards');
const currentTime = ref('');
const currentDate = ref('');

// Estados de filtro
const selectedCategory = ref(null);
const filterLoading = ref(false);

// Estados de paginação para tela cheia
const currentPage = ref(0);
const cardsPerPage = ref(6);

// Timers
let autoUpdateInterval = null;
let timeUpdateTimer = null;
let fullscreenRotationTimer = null;

// Computed para última atualização
const lastUpdate = computed(() => {
  if (servidores.value.length === 0) return null;
  const dates = servidores.value.map(s => new Date(s.validation_date));
  return new Date(Math.max(...dates));
});

// Computed para servidores filtrados
const filteredServidores = computed(() => {
  if (!selectedCategory.value) {
    return servidores.value;
  }

  return servidores.value.filter((servidor) => {
    const status = statusText(servidor.status);
    if (selectedCategory.value === "Funcionamento Parcial") {
      return servidor.status === 2 || servidor.status === 3;
    }
    return status === selectedCategory.value;
  });
});

// Computed para exibição
const displayServidores = computed(() => {
  return filteredServidores.value;
});

// Computed para paginação em tela cheia
const currentPageServidores = computed(() => {
  const start = currentPage.value * cardsPerPage.value;
  return filteredServidores.value.slice(start, start + cardsPerPage.value);
});

const totalPages = computed(() => {
  return Math.ceil(filteredServidores.value.length / cardsPerPage.value);
});

// Computed para porcentagem de disponibilidade
const availabilityPercentage = computed(() => {
  if (servidores.value.length === 0) return 0;
  const available = servidores.value.filter(s => s.status === 1).length;
  return Math.round((available / servidores.value.length) * 100);
});

// Dados dos gráficos
const statusChartData = computed(() => ({
  labels: ['Pleno Funcionamento', 'Funcionamento Parcial', 'Fora de Operação'],
  datasets: [{
    data: [
      getStatusCount(1),
      getStatusCount(2, 3),
      getStatusCount(4)
    ],
    backgroundColor: [
      'rgba(34, 197, 94, 0.8)',
      'rgba(251, 191, 36, 0.8)',
      'rgba(239, 68, 68, 0.8)'
    ],
    borderColor: [
      'rgba(34, 197, 94, 1)',
      'rgba(251, 191, 36, 1)',
      'rgba(239, 68, 68, 1)'
    ],
    borderWidth: 2
  }]
}));

const unitsBarChartData = computed(() => ({
  labels: servidores.value.map(s => s.unidade_nome.substring(0, 10)),
  datasets: [{
    label: 'Status',
    data: servidores.value.map(s => s.status),
    backgroundColor: servidores.value.map(s => {
      if (s.status === 1) return 'rgba(34, 197, 94, 0.8)';
      if (s.status === 2 || s.status === 3) return 'rgba(251, 191, 36, 0.8)';
      return 'rgba(239, 68, 68, 0.8)';
    }),
    borderColor: servidores.value.map(s => {
      if (s.status === 1) return 'rgba(34, 197, 94, 1)';
      if (s.status === 2 || s.status === 3) return 'rgba(251, 191, 36, 1)';
      return 'rgba(239, 68, 68, 1)';
    }),
    borderWidth: 2
  }]
}));

const trendChartData = computed(() => ({
  labels: ['6h atrás', '5h atrás', '4h atrás', '3h atrás', '2h atrás', '1h atrás', 'Agora'],
  datasets: [{
    label: 'Disponibilidade (%)',
    data: [85, 88, 92, 89, 91, 94, availabilityPercentage.value],
    borderColor: 'rgba(59, 130, 246, 1)',
    backgroundColor: 'rgba(59, 130, 246, 0.1)',
    tension: 0.4,
    fill: true
  }]
}));

// Opções dos gráficos
const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'bottom',
      labels: {
        color: 'rgb(107, 114, 128)',
        font: { size: 14 }
      }
    }
  }
};

const barChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false }
  },
  scales: {
    x: {
      ticks: { 
        color: 'rgb(107, 114, 128)',
        maxRotation: 45
      }
    },
    y: {
      ticks: { color: 'rgb(107, 114, 128)' },
      min: 0,
      max: 4
    }
  }
};

const lineChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false }
  },
  scales: {
    x: {
      ticks: { color: 'rgb(107, 114, 128)' }
    },
    y: {
      ticks: { color: 'rgb(107, 114, 128)' },
      min: 0,
      max: 100
    }
  }
};

// Funções principais
async function fetchServidores() {
  isLoading.value = true;
  error.value = null;

  try {
    const response = await apiClient.get("/api/v1/scan/link");
    servidores.value = response.data;
  } catch (err) {
    error.value = "Erro ao carregar os servidores. Tente novamente mais tarde.";
    console.error(err);
  } finally {
    isLoading.value = false;
  }
}

const updateTime = () => {
  const now = new Date();
  currentTime.value = now.toLocaleTimeString('pt-BR');
  currentDate.value = now.toLocaleDateString('pt-BR', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

const setView = (view) => {
  currentView.value = view;
};

const toggleFullscreen = () => {
  isFullscreenMode.value = !isFullscreenMode.value;
  if (isFullscreenMode.value) {
    startFullscreenMode();
  } else {
    stopFullscreenMode();
  }
};

const startFullscreenMode = () => {
  currentPage.value = 0;
  fullscreenView.value = 'cards';
  
  // Rotação entre views
  fullscreenRotationTimer = setInterval(() => {
    if (fullscreenView.value === 'cards') {
      if (currentPage.value + 1 >= totalPages.value) {
        fullscreenView.value = 'charts';
        currentPage.value = 0;
      } else {
        currentPage.value++;
      }
    } else {
      fullscreenView.value = 'cards';
      currentPage.value = 0;
    }
  }, 8000);

  // Tentar entrar em tela cheia
  const element = document.documentElement;
  if (element && element.requestFullscreen && !document.fullscreenElement) {
    element.requestFullscreen().catch(() => {});
  }
};

const stopFullscreenMode = () => {
  if (fullscreenRotationTimer) {
    clearInterval(fullscreenRotationTimer);
    fullscreenRotationTimer = null;
  }

  if (document.fullscreenElement && document.exitFullscreen) {
    document.exitFullscreen().catch(() => {});
  }
};

// Funções auxiliares
const showGlobeButton = (status, linkType) => {
  if (status === 1 && linkType === "primary") return true;
  if (status === 2 && linkType === "primary") return true;
  if (status === 3 && linkType === "secondary") return true;
  return false;
};

const openLink = (dns) => {
  window.open(`https://${dns}/Place`, "_blank");
};

const getLinkCardClass = (status, linkType, servidor) => {
  if (linkType === "secondary" && (!servidor?.secondaryIP || servidor?.secondaryName === null)) {
    return "bg-gray-300 dark:bg-gray-700 border border-gray-400 dark:border-gray-600 shadow hover:shadow-lg transition";
  }

  if (status === 1) {
    return "bg-gradient-to-r from-green-50 to-green-100 dark:from-green-800 dark:to-green-700 border border-green-300 dark:border-green-600 shadow-lg hover:shadow-xl transition";
  } else if (status === 2 && linkType === "primary") {
    return "bg-gradient-to-r from-green-50 to-green-100 dark:from-green-800 dark:to-green-700 border border-green-300 dark:border-green-600 shadow-lg hover:shadow-xl transition";
  } else if (status === 3 && linkType === "secondary") {
    return "bg-gradient-to-r from-green-50 to-green-100 dark:from-green-800 dark:to-green-700 border border-green-300 dark:border-green-600 shadow-lg hover:shadow-xl transition";
  } else if (status === 4 || (status === 2 && linkType === "secondary") || (status === 3 && linkType === "primary")) {
    return "bg-gradient-to-r from-red-50 to-red-100 dark:from-red-800 dark:to-red-700 border border-red-300 dark:border-red-600 shadow-lg hover:shadow-xl transition";
  }

  return "bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700 border border-gray-300 dark:border-gray-600 shadow hover:shadow-lg transition";
};

const statusText = (status) => {
  const statuses = {
    1: "Pleno Funcionamento",
    2: "Funcionamento Parcial",
    3: "Funcionamento Parcial",
    4: "Fora de Operação",
  };
  return statuses[status] || "Desconhecido";
};

const getStatusIcon = (category) => {
  const icons = {
    "Pleno Funcionamento": MonitorCheck,
    "Funcionamento Parcial": Monitor,
    "Fora de Operação": MonitorX,
  };
  return icons[category] || Monitor;
};

const getStatusIconColor = (category) => {
  const colors = {
    "Pleno Funcionamento": "text-green-600 dark:text-green-400",
    "Funcionamento Parcial": "text-yellow-600 dark:text-yellow-400",
    "Fora de Operação": "text-red-600 dark:text-red-400",
  };
  return colors[category] || "text-gray-600 dark:text-gray-400";
};

const getStatusTextColor = (category) => {
  const colors = {
    "Pleno Funcionamento": "text-green-600 dark:text-green-400",
    "Funcionamento Parcial": "text-yellow-600 dark:text-yellow-400",
    "Fora de Operação": "text-red-600 dark:text-red-400",
  };
  return colors[category] || "text-gray-900 dark:text-white";
};

const getStatusBgColor = (category) => {
  const colors = {
    "Pleno Funcionamento": "bg-green-100 dark:bg-green-900/30",
    "Funcionamento Parcial": "bg-yellow-100 dark:bg-yellow-900/30",
    "Fora de Operação": "bg-red-100 dark:bg-red-900/30",
  };
  return colors[category] || "bg-gray-100 dark:bg-gray-700";
};

const getStatusSummary = () => {
  const summary = {
    "Pleno Funcionamento": 0,
    "Funcionamento Parcial": 0,
    "Fora de Operação": 0,
  };

  servidores.value.forEach((servidor) => {
    if (servidor.status === 1) {
      summary["Pleno Funcionamento"]++;
    } else if (servidor.status === 2 || servidor.status === 3) {
      summary["Funcionamento Parcial"]++;
    } else if (servidor.status === 4) {
      summary["Fora de Operação"]++;
    }
  });

  return summary;
};

const getStatusCount = (...statuses) => {
  return servidores.value.filter(s => statuses.includes(s.status)).length;
};

const getFullscreenStatusIndicatorClass = (status) => {
  switch (status) {
    case 1: return 'bg-green-500';
    case 2:
    case 3: return 'bg-yellow-500';
    case 4: return 'bg-red-500';
    default: return 'bg-gray-500';
  }
};

const getFullscreenCardBorderClass = (status) => {
  switch (status) {
    case 1: return 'border-green-500/50 shadow-green-500/20 shadow-lg';
    case 2:
    case 3: return 'border-yellow-500/50 shadow-yellow-500/20 shadow-lg';
    case 4: return 'border-red-500/50 shadow-red-500/20 shadow-lg';
    default: return 'border-white/10';
  }
};

const getFullscreenLinkCardClass = (status, linkType, servidor) => {
  if (linkType === "secondary" && (!servidor?.secondaryIP || servidor?.secondaryName === null)) {
    return "bg-gray-500/20 border-gray-400/30";
  }

  if (status === 1) {
    return "bg-green-500/20 border-green-400/30";
  } else if (status === 2 && linkType === "primary") {
    return "bg-green-500/20 border-green-400/30";
  } else if (status === 3 && linkType === "secondary") {
    return "bg-green-500/20 border-green-400/30";
  } else if (status === 4 || (status === 2 && linkType === "secondary") || (status === 3 && linkType === "primary")) {
    return "bg-red-500/20 border-red-400/30";
  }

  return "bg-gray-500/20 border-gray-400/30";
};

const goToUnidadeDetalhes = (unidadeId) => {
  router.push(`/unidades/detalhes/${unidadeId}`);
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleString("pt-BR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const filterByCategory = async (category) => {
  selectedCategory.value = category;
  filterLoading.value = true;
  setTimeout(() => {
    filterLoading.value = false;
  }, 500);
};

const clearFilter = () => {
  selectedCategory.value = null;
  filterLoading.value = false;
};

const startAutoUpdate = () => {
  // O backend recebe varreduras a cada 5 min e cacheia a resposta por 60s;
  // consultar mais rápido que isso só gera carga sem dado novo.
  autoUpdateInterval = setInterval(() => {
    if (document.hidden) return; // aba em segundo plano: não consome API
    fetchServidores();
  }, 60 * 1000); // 60 segundos
};

onMounted(() => {
  fetchServidores();
  startAutoUpdate();
  updateTime();
  timeUpdateTimer = setInterval(updateTime, 1000);
});

onUnmounted(() => {
  if (autoUpdateInterval) {
    clearInterval(autoUpdateInterval);
  }
  if (timeUpdateTimer) {
    clearInterval(timeUpdateTimer);
  }
  stopFullscreenMode();
});
</script>
